import * as Sentry from '@sentry/react';
import ReclamationService from '../services/reclamation.service'
import { addNotification } from './notification.actions'
import {
  LOAD_RECLAMATIONS,
  LOAD_RECLAMATIONS_SUCCESS,
  RECLAMATION_ERROR,
  LOAD_RECLAMATION,
  LOAD_RECLAMATION_SUCCESS,
  LOAD_CATEGORIES,
  LOAD_STATUSES,
  LOAD_CATEGORIES_SUCCESS,
  LOAD_STATUSES_SUCCESS,
  LOAD_RECLAMATION_FILES
} from './types'
import FilesService from '../services/files.service'
import { saveAs } from 'file-saver'
import { removeHtml } from '../common/utils'

// Převede chybu odeslání na srozumitelný důvod pro klienta
function getSubmitErrorMessage(e) {
  const status = e.response && e.response.status
  const serverMessage = e.response && e.response.data && e.response.data.message

  if (!e.response) {
    return 'Spojení se serverem se přerušilo (nejčastěji kvůli slabému signálu během nahrávání příloh). Zkontrolujte prosím připojení k internetu a zkuste reklamaci odeslat znovu.'
  }
  if (status === 413) {
    return serverMessage || 'Přílohy jsou příliš velké. Odeberte prosím některé soubory nebo přiložte menší fotografie.'
  }
  if (status === 400 && serverMessage) {
    return serverMessage
  }
  if ([502, 503, 504].includes(status)) {
    return 'Nahrávání příloh trvalo příliš dlouho a server ho přerušil. Zkuste prosím přiložit méně souborů nebo použít rychlejší připojení (např. Wi-Fi).'
  }
  return `Na serveru došlo k chybě (kód ${status}). Zkuste to prosím za chvíli znovu.`
}

// Vrací { error } s důvodem, proč se reklamaci nepodařilo odeslat, jinak {}
export function submitReclamation(data, files, sessionId, onUploadProgress) {
  return async dispatch => {
    const fileDetails = files.map(file => ({ name: file.name, type: file.type, size: file.size }))

    try {
      Sentry.setTag('session_id', sessionId);
      Sentry.setTag('files_count', files.length);
      Sentry.addBreadcrumb({
        category: 'reclamation',
        message: 'Reclamation submission started',
        level: 'info',
        data: {
          session_id: sessionId,
          residence: data.rezidence,
          category: data.kategorie,
          files: fileDetails,
          total_size: files.reduce((sum, file) => sum + file.size, 0)
        }
      });

      const response = await ReclamationService.submit(data, files, onUploadProgress)

      Sentry.setTag('reclamation_id', response.data.id || 'unknown');
      Sentry.captureMessage('Reclamation submitted successfully', {
        level: 'info',
        tags: {
          session_id: sessionId,
          reclamation_id: response.data.id || 'unknown',
          residence_id: data.rezidence,
          files_count: files.length
        },
        extra: {
          folder: response.data.folder,
          submission_time: new Date().toISOString()
        }
      });

      return {}
    } catch (e) {
      const errorMessage = getSubmitErrorMessage(e)

      Sentry.setContext('reclamation_data', {
        session_id: sessionId,
        reclamation: data,
        files_count: files.length,
        files: fileDetails,
        status: e.response && e.response.status,
        response_data: e.response && e.response.data,
        shown_message: errorMessage
      });
      Sentry.captureException(e, {
        tags: {
          action: 'submitReclamation',
          error_type: e.response ? `http_${e.response.status}` : 'network',
          session_id: sessionId
        },
      });

      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
      dispatch(addNotification('Reklamaci se nepodařilo odeslat.', false))

      return { error: errorMessage }
    }
  }
}

export function editReclamation(data) {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATIONS })

      await ReclamationService.edit(data)
      const request = await ReclamationService.list()

      dispatch({ type: LOAD_RECLAMATIONS_SUCCESS, payload: request })

      dispatch(addNotification('Reklamace upravena!', true))
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
      dispatch(addNotification('Reklamaci se nepodařilo upravit.', false))
    }
  }
}


export function confirm(data) {
  return async dispatch => {
    try {
      await ReclamationService.confirm(data)
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
    }
  }
}

export function loadReclamations() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATIONS })

      const request = await ReclamationService.list()

      dispatch({
        type: LOAD_RECLAMATIONS_SUCCESS,
        payload: request
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Reklamaci se nepodařilo načíst.', false))
    }
  }
}

export function loadReclamation(id) {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATION })

      const request = await ReclamationService.getByID(id)

      dispatch({
        type: LOAD_RECLAMATION_SUCCESS,
        payload: request
      })

      const filesRequest = await FilesService.getReclamationFiles(
        'reklamace/' + request.data.reclamation.folder
      )

      dispatch({
        type: LOAD_RECLAMATION_FILES,
        payload: filesRequest
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Reklamaci se nepodařilo načíst.', false))
    }
  }
}

export function contactCompany(id) {
  return async dispatch => {
    try {
      await ReclamationService.contactCompany(id)

      dispatch(addNotification('Email odeslan!', true))
      dispatch({ type: LOAD_RECLAMATIONS })

      const request = await ReclamationService.list()

      dispatch({
        type: LOAD_RECLAMATIONS_SUCCESS,
        payload: request
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Email se nepodařilo odeslat.', false))
    }
  }
}

export function loadCategories() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_CATEGORIES })

      const categories = await ReclamationService.getCategories()

      dispatch({
        type: LOAD_CATEGORIES_SUCCESS,
        payload: categories
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Kategorie se nepodařilo načíst.', false))
    }
  }
}

export function loadStatuses() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_STATUSES })

      const statuses = await ReclamationService.getStatuses()

      dispatch({
        type: LOAD_STATUSES_SUCCESS,
        payload: statuses
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Kategorie se nepodařilo načíst.', false))
    }
  }
}

export function exportData(ids) {
  return async dispatch => {
    try {
      const request = await ReclamationService.getByIDs(ids)
      const date = new Date()
      const fullDate =
        date.getDate() + '_' + date.getMonth() + '_' + date.getFullYear()
      let csvContent =
        '\ufeff' +
        'Interní evidenční číslo;Bytový dům;Stav reklamace;Popis závady\r\n'

      request.data.reclamations.forEach(function (r) {
        csvContent += `${r.id};${r.rezidence};${r.status};"${removeHtml(
          r.internalText
        )}"\r\n`
      })

      var blob = new Blob([csvContent], {
        type: 'text/plain;charset=utf-8'
      })

      saveAs(blob, `export_reklamace_${fullDate}.csv`)

      dispatch(addNotification('Export úspěšný.', true))
    } catch (e) {
      dispatch(addNotification('Export se nezdařil.', false))
    }
  }
}
